import React, { useMemo, useRef } from 'react'
import type { PlotViewport } from '../contract'
import { usePlotGraphicBudget } from '../_shared/usePlotGraphicBudget'

import { RadarData, RadarProps } from '@digital-twins/slide-schemas/generated/plot-types'

import { formatHeatValue } from '../heat-grid/format'

/**
 * `radar`: the alternative view of a heat grid (plots plan Q). Axes are the
 * measures, one polygon per series, on the one scale Python stamped.
 *
 * - Outlines when two or more series are drawn (filled only for one, or when
 *   `props.fill` says so), so no polygon hides another.
 * - The scale's min and max are printed on the first axis (visible ticks).
 * - A missing value is a gap in the outline, never a point at 0.
 * - A low-base series draws dashed.
 * - Series colours are the stamped identity slots (`--slide-chart-N`).
 *
 * Pure SVG: no chart library, nothing from the frontend.
 */

export interface RadarPlotData extends Omit<RadarData, 'labels'> {
  labels: Partial<RadarData['labels']>
}

export interface RadarPlotProps {
  viewport?: PlotViewport
  data: RadarPlotData
  props?: RadarProps
  testIdPrefix?: string
}

const VIEW = 360
const CENTER = VIEW / 2
const RADIUS = 118
const TICKS = 4
/** Below this the scaled axis labels no longer clear the card. The 360 design size scales down into a shorter exhibit viewport. */
const MIN_FIT = 160

function seriesColor(slot: number | null | undefined): string {
  if (typeof slot !== 'number') {
    return 'var(--slide-text-muted, #737373)'
  }
  return `var(--slide-chart-${slot}, var(--slide-chart-1, #0ebfa5))`
}

function point(angle: number, radius: number): [number, number] {
  return [CENTER + radius * Math.sin(angle), CENTER - radius * Math.cos(angle)]
}

/** The drawn outline as runs of consecutive present values (a null breaks the run). */
function runs(values: ReadonlyArray<number | null>): Array<Array<number>> {
  const out: Array<Array<number>> = []
  let current: Array<number> = []
  values.forEach((value, index) => {
    if (value === null) {
      if (current.length > 0) {
        out.push(current)
      }
      current = []
    } else {
      current.push(index)
    }
  })
  if (current.length > 0) {
    out.push(current)
  }
  // The outline is closed: a run that reaches the last axis continues into
  // the run that starts at the first one.
  const last = out[out.length - 1]
  if (out.length > 1 && out[0][0] === 0 && last[last.length - 1] === values.length - 1) {
    out[0] = [...last, ...out[0]]
    out.pop()
  }
  return out
}

export function RadarPlot(props: RadarPlotProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const graphicRef = useRef<SVGSVGElement>(null)
  const graphics = useMemo(() => [graphicRef], [])
  const budget = usePlotGraphicBudget(props.viewport, rootRef, graphics)
  const { data, testIdPrefix = 'radar' } = props
  const renderProps: RadarProps = props.props ?? {}
  const axes = data.axes
  const count = axes.length
  const { min, max } = data.scale
  const span = max - min
  const locale = renderProps.locale
  const fillMode = renderProps.fill ?? 'auto'
  const filled = fillMode === 'all' || (fillMode === 'auto' && data.series.length === 1)
  const angleOf = (index: number) => (2 * Math.PI * index) / Math.max(count, 1)
  const radiusOf = (value: number) => (span > 0 ? (Math.max(min, Math.min(max, value)) - min) / span : 0) * RADIUS
  const hasData = data.series.some((series) => series.values.some((value) => typeof value === 'number'))

  if (!hasData || count < 3) {
    return (
      <div data-testid={`${testIdPrefix}-empty`} style={{ fontSize: 13, color: 'var(--slide-text-secondary)' }}>
        {data.labels.plot_no_data ?? null}
      </div>
    )
  }

  const tickFormat = { valueFormat: 'mean' as const, decimals: Number.isInteger(min) && Number.isInteger(max) ? 0 : data.decimals }
  const fitted = budget ? Math.min(420, budget.height, budget.width) : null

  return (
    <div ref={rootRef} data-viewport-infeasible={fitted != null ? fitted < MIN_FIT : undefined} data-testid={testIdPrefix} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <svg ref={graphicRef}
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        role="img"
        data-testid={`${testIdPrefix}-svg`}
        style={{ width: fitted != null ? fitted : '100%', height: fitted != null ? fitted : undefined, alignSelf: budget ? 'center' : undefined, maxHeight: 420, overflow: 'visible' }}
      >
        {Array.from({ length: TICKS }, (_, index) => {
          const radius = (RADIUS * (index + 1)) / TICKS
          const ring = axes.map((_axis, axisIndex) => point(angleOf(axisIndex), radius).join(',')).join(' ')
          return (
            <polygon
              key={`ring-${index}`}
              points={ring}
              fill="none"
              stroke="var(--slide-border-soft, #e2e2e2)"
              strokeWidth={1}
            />
          )
        })}
        {axes.map((axis, index) => {
          const [x, y] = point(angleOf(index), RADIUS)
          const [lx, ly] = point(angleOf(index), RADIUS + 16)
          const anchor = Math.abs(lx - CENTER) < 4 ? 'middle' : lx > CENTER ? 'start' : 'end'
          return (
            <g key={axis.id} data-testid={`${testIdPrefix}-axis`} data-axis-id={axis.id}>
              <line x1={CENTER} y1={CENTER} x2={x} y2={y} stroke="var(--slide-border-soft, #e2e2e2)" strokeWidth={1} />
              <text
                x={lx}
                y={ly}
                textAnchor={anchor}
                dominantBaseline="middle"
                fontSize={11}
                fill="var(--slide-text-secondary, #384242)"
              >
                <title>{axis.label}</title>
                {axis.label}
              </text>
            </g>
          )
        })}
        <text
          data-testid={`${testIdPrefix}-tick-min`}
          x={CENTER + 4}
          y={CENTER - 2}
          fontSize={9}
          fill="var(--slide-text-muted, #737373)"
        >
          {formatHeatValue(min, tickFormat, locale)}
        </text>
        <text
          data-testid={`${testIdPrefix}-tick-max`}
          x={CENTER + 4}
          y={CENTER - RADIUS - 2}
          fontSize={9}
          fill="var(--slide-text-muted, #737373)"
        >
          {formatHeatValue(max, tickFormat, locale)}
        </text>
        {data.series.map((series) => {
          const color = seriesColor(series.colorSlot)
          const values = series.values.map((value) => (typeof value === 'number' ? value : null))
          const complete = values.every((value) => value !== null)
          const dash = series.lowBase ? '4 3' : undefined
          return (
            <g key={series.id} data-testid={`${testIdPrefix}-series`} data-series-id={series.id} data-low-base={series.lowBase ? 'true' : 'false'}>
              {complete ? (
                <polygon
                  points={values.map((value, index) => point(angleOf(index), radiusOf(value as number)).join(',')).join(' ')}
                  fill={filled ? color : 'none'}
                  fillOpacity={filled ? 0.15 : undefined}
                  stroke={color}
                  strokeWidth={2}
                  strokeDasharray={dash}
                />
              ) : (
                runs(values).map((run) => (
                  <polyline
                    key={`run-${run[0]}`}
                    data-testid={`${testIdPrefix}-gap-run`}
                    points={run.map((index) => point(angleOf(index), radiusOf(values[index] as number)).join(',')).join(' ')}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray={dash}
                  />
                ))
              )}
              {values.map((value, index) =>
                value === null ? null : (
                  <circle
                    key={`pt-${index}`}
                    cx={point(angleOf(index), radiusOf(value))[0]}
                    cy={point(angleOf(index), radiusOf(value))[1]}
                    r={3}
                    fill={color}
                  >
                    <title>{`${series.label}, ${axes[index].label}: ${formatHeatValue(value, { valueFormat: 'mean', decimals: data.decimals }, locale)}`}</title>
                  </circle>
                ),
              )}
            </g>
          )
        })}
      </svg>
      <div data-testid={`${testIdPrefix}-legend`} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--slide-text-secondary)' }}>
        {data.series.map((series) => (
          <span key={series.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <i
              style={{
                display: 'inline-block',
                width: 14,
                height: 0,
                borderTop: `2px ${series.lowBase ? 'dashed' : 'solid'} ${seriesColor(series.colorSlot)}`,
              }}
            />
            {series.label}
          </span>
        ))}
      </div>
    </div>
  )
}
